import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdventureSummary } from '@gm-binder/shared';
import { api, mediaUrl } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Badge,
  Button,
  MediaImage,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { useUiStore } from '../store/uiStore';
import styles from './HomePage.module.css';

export function HomePage() {
  const [adventures, setAdventures] = useState<AdventureSummary[] | null>(null);
  const navigate = useNavigate();
  const toast = useUiStore((s) => s.toast);

  useEffect(() => {
    void (async () => {
      try {
        setAdventures(await api.listAdventures());
      } catch {
        setAdventures([]);
      }
    })();
  }, []);

  const createAdventure = async () => {
    try {
      const adv = await api.createAdventure({ title: 'Nuova avventura' });
      navigate(`/library/${adv.slug}`);
    } catch {
      toast('Creazione fallita', 'danger');
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="La tua libreria"
        title="One-shot"
        subtitle="Crea, prepara e dai vita alle tue avventure."
        actions={
          <Button variant="primary" icon="✦" onClick={createAdventure}>
            Nuova avventura
          </Button>
        }
      />

      {adventures == null ? (
        <LoadingState />
      ) : adventures.length === 0 ? (
        <EmptyState
          title="Nessuna avventura"
          description="Inizia creando la tua prima one-shot."
          actions={
            <Button variant="primary" onClick={createAdventure}>
              Crea avventura
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {adventures.map((a) => (
            <Link key={a.slug} to={`/library/${a.slug}`} className={styles.cardLink}>
              <Card padded={false} interactive className={styles.card}>
                <MediaImage
                  src={mediaUrl(a.slug, a.cover)}
                  alt={a.title}
                  fallback="🕯️"
                  className={styles.cover}
                />
                <div className={styles.cardBody}>
                  <h3 className={styles.title}>{a.title}</h3>
                  {a.subtitle && <p className={styles.subtitle}>{a.subtitle}</p>}
                  <div className={styles.meta}>
                    {a.meta.level != null && <Badge tone="gold">Lv {a.meta.level}</Badge>}
                    {a.meta.playerCount && <Badge>👥 {a.meta.playerCount}</Badge>}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  );
}
